/**
 * @license Copyright © HatioLab Inc. All rights reserved.
 */

import { css, html, LitElement, PropertyValues } from 'lit'
import { property, query, state } from 'lit/decorators.js'

import { Scene } from '@hatiolab/things-scene'
import { ScopedElementsMixin } from '@open-wc/scoped-elements'
import { ScrollbarStyles } from '@operato/styles'
import { i18next } from '@operato/i18n'

import { Pallet, PalletItem } from '../../types.js'
import { ComponentDetail } from './component-detail.js'

const noImage = new URL('../../../../icons/components/no-image.png', import.meta.url).href

export class ComponentMenu extends ScopedElementsMixin(LitElement) {
  static styles = [
    ScrollbarStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-content: stretch;

        background-color: var(--component-menu-background-color, var(--md-sys-color-secondary-container));
        color: var(--component-menu-color, var(--md-sys-color-on-secondary-container));
        margin: 0px;
        padding: 0px;

        width: 180px;
        height: 100%;

        overflow: visible;

        border: 2px solid var(--component-menu-border-color, var(--md-sys-color-secondary));
        box-sizing: border-box;

        position: absolute;
        top: 0px;

        z-index: 1;
      }

      h2 {
        background-color: var(--component-menu-border-color, var(--md-sys-color-secondary));
        padding: 1px 5px;
        margin: 0;
        font: var(--component-menu-title);
        color: var(--md-sys-color-on-secondary);
        text-transform: capitalize;
      }

      [templates] {
        flex: 1;

        display: block;
        margin: 0;
        padding: 0;
        overflow-y: auto;

        background-color: var(--component-menu-background-color, var(--md-sys-color-secondary-container));
        color: var(--component-menu-color, var(--md-sys-color-on-secondary-container));
      }

      [template] {
        display: flex;
        align-items: center;
        min-height: var(--component-menu-item-icon-size);
        padding: 0 5px 0 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        font-size: 11px;
        color: var(--component-menu-item-color, var(--md-sys-color-on-secondary-container));
        text-transform: capitalize;
      }

      [template]:hover,
      [template]:focus {
        color: var(--component-menu-item-hover-color, var(--md-sys-color-secondary));
        font-weight: bold;
        cursor: pointer;
      }

      [template] img {
        margin: 5px;
        width: var(--component-menu-item-icon-size);
        height: var(--component-menu-item-icon-size);
      }

      component-detail {
        position: absolute;
        top: 0;
        left: 180px;
        height: 100%;
        outline: none;
      }

      component-detail[hidden] {
        display: none;
      }
    `
  ]

  @property({ type: Object }) groups: Pallet[] = []
  @property({ type: Object }) scene: Scene | null = null
  @property({ type: String }) group: string | null = ''

  @state() templates: PalletItem[] = []
  @state() template: PalletItem | any

  @query('component-detail') detail!: HTMLElement

  static get scopedElements() {
    return {
      'component-detail': ComponentDetail
    }
  }

  render() {
    return this.group
      ? html`
          <h2 onclick=${(e: MouseEvent) => e.stopPropagation()}>${this.group == 'chartAndGauge' ? 'chart' : this.group} list</h2>

          <div templates @mouseover=${(e: MouseEvent) => this.onHoverComponent(e)}>
            ${(this.templates || []).map(template => {
              const { type } = template

              return html`
                <div @click=${this.onClickTemplate} data-type=${type} template><img src=${String(this.templateIcon(template))} />${i18next.t(`component.${type}`)}</div>
              `
            })}
          </div>

          <component-detail
            tabindex="-1"
            @focusout=${() => {
              this.template = null
            }}
            .template=${this.template}
            hidden
          >
          </component-detail>
        `
      : html``
  }

  updated(changes: PropertyValues<this>) {
    if (changes.has('group')) {
      if (!this.group) {
        this.templates = []
        this.setAttribute('hidden', '')
      } else {
        this.templates = this.groups.find((g: Pallet) => g.name === this.group)?.templates || []
        this.removeAttribute('active')
      }
    }

    if (changes.has('template')) {
      this.template && this.template.about ? this.detail.removeAttribute('hidden') : this.detail.setAttribute('hidden', '')
    }
  }

  findTemplate(type: string | null | undefined) {
    this.template = type && this.templates.find(template => template.type == type)
  }

  onHoverComponent(e: MouseEvent) {
    const button = e.target as HTMLElement
    this.findTemplate(button!.closest('[data-type]')?.getAttribute('data-type'))
  }

  onClickTemplate(e: MouseEvent) {
    var item = e.target as HTMLElement
    var type = item.closest('[data-type]')?.getAttribute('data-type')

    if (!type) {
      return
    }

    if (this.scene) {
      this.template = this.templates.find(template => template.type == type)
      this.template && this.scene.startAddMode(JSON.parse(JSON.stringify(this.template.model)))
    }

    this.group = null
  }

  templateIcon(template: PalletItem) {
    return template.icon || noImage
  }
}
